// Слой листов «Разметки страницы»: белые листы A4 с тенью и промежутком, номера страниц внизу по центру (TNR 14,
// как колонтитул writer), сноски внизу листа; листы перед текстом (титул, задание, лист замечаний, реферат) - каждый
// отдельным листом A4; оглавление - листами с номерами страниц из этой же разметки.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Ctx } from '../../core/registry';
import { useApp } from '../../core/store';
import { useNumbering } from '../../core/editor/numberingPlugin';
import { gotoNode } from '../../core/docIndex';
import { tocOn } from '../sto_intel/data';
import { pagesStore, usePages } from './store';
import { BODY_H, BODY_TOP, FOOTER_BOTTOM, LINE_14_SINGLE, M_BOTTOM, M_TOP, PAGE_H, STRIDE, TEXT_H } from './geometry';
void BODY_TOP;

/** пустая строка / заполнитель интерфейса (не текст отчёта) */
const NB = ' ';

// ------------------------------------------------------------------------------------------------ сноски
function inlineText(content: any[] | undefined): React.ReactNode[] {
  return (content || []).map((n: any, i: number) => {
    if (n.type === 'text') {
      let el: React.ReactNode = n.text;
      for (const m of n.marks || []) {
        if (m.type === 'bold') el = <b key={i}>{el}</b>;
        else if (m.type === 'italic') el = <i key={i}>{el}</i>;
        else if (m.type === 'underline') el = <u key={i}>{el}</u>;
        else if (m.type === 'superscript') el = <sup key={i}>{el}</sup>;
        else if (m.type === 'subscript') el = <sub key={i}>{el}</sub>;
        else if (m.type === 'code') el = <code key={i}>{el}</code>;
      }
      return <span key={i}>{el}</span>;
    }
    if (n.type === 'mathInline') return <span key={i} className="pg-fn-math">{n.attrs?.latex}</span>;
    return null;
  });
}

/** Сноски документа: id -> содержимое (из узлов footnote). Пересчёт - только когда меняется список листов
 *  (сноски на листах) или документ, а не при каждом пересчёте разметки. */
function useFootnotes(): Map<string, any[]> {
  const editor = useApp((s) => s.editor);
  const main = usePages((s) => s.main);
  const rev = useApp((s) => s.revision);
  return useMemo(() => {
    const m = new Map<string, any[]>();
    editor?.state.doc.descendants((n) => {
      if (n.type.name === 'footnote' && n.attrs.id) m.set(n.attrs.id, n.attrs.content || []);
      return !n.isTextblock || n.childCount > 0;
    });
    return m;
  }, [editor, main, rev]);
}

function Footnote({ id, num, content, measure }: { id: string; num: number | string; content: any[]; measure?: boolean }) {
  return (
    <p className="pg-fn" data-fn-measure={measure ? id : undefined}><sup>{num}</sup>{' '}{inlineText(content)}</p>
  );
}

// ------------------------------------------------------------------------------------------------ основной текст
/** Листы основного текста (за текстом редактора) и номера страниц. */
export function PagesLayer() {
  const main = usePages((s) => s.main);
  const front = usePages((s) => s.frontCount);
  const nb = useNumbering();
  const fns = useFootnotes();
  const ids = [...fns.keys()];
  return (
    <div className="pg-sheets" aria-hidden data-testid="pages-layer">
      {main.map((p, i) => (
        <div key={i} className="pg-page" style={{ top: i * STRIDE }} data-page={front + i + 1}>
          {p.fns.length > 0 && (
            <div className="pg-fns" style={{ top: BODY_TOP + BODY_H - p.fnH }}>
              <div className="pg-fn-sep"><span /></div>
              {p.fns.map((id) => <Footnote key={id} id={id} num={nb.footnotes.get(id) ?? '*'} content={fns.get(id) || []} />)}
            </div>
          )}
          <div className="pg-num" style={{ top: FOOTER_BOTTOM - LINE_14_SINGLE, height: LINE_14_SINGLE }}>{front + i + 1}</div>
        </div>
      ))}
      <div className="pg-fn-measure" aria-hidden>
        <div className="pg-fn-sep pg-fn-sep-measure"><span /></div>
        {ids.map((id) => <Footnote key={id} id={id} num={nb.footnotes.get(id) ?? '*'} content={fns.get(id) || []} measure />)}
      </div>
    </div>
  );
}

/** Слой над текстом: полосы, закрывающие рамку листинга в разрыве страницы (белые - поля листа, серая - промежуток). */
export function PagesOverlay() {
  const masks = usePages((s) => s.masks);
  if (!masks.length) return null;
  return (
    <div className="pg-over" aria-hidden>
      {masks.map((m, i) => {
        const q = Math.floor(m.bottom / STRIDE);            // лист, где продолжается листинг
        const gapTop = q * STRIDE - (STRIDE - PAGE_H);        // низ предыдущего листа
        const gapBottom = q * STRIDE;
        // куски полосы: низ предыдущего листа, промежуток, верх листа продолжения (только в пределах полосы)
        const parts = [
          { cls: 'pg-mask-page', top: m.top, bottom: Math.min(m.bottom, gapTop) },
          { cls: 'pg-mask-gap', top: Math.max(m.top, gapTop), bottom: Math.min(m.bottom, gapBottom) },
          { cls: 'pg-mask-page', top: Math.max(m.top, gapBottom), bottom: m.bottom },
        ].filter((x) => x.bottom > x.top);
        // только края рамки (левая и правая линии): номер страницы и сноски под листингом не закрываются
        const edges = [m.left, m.left + m.width - 8];
        return (
          <div key={i}>
            {edges.map((x, j) => (
              <div key={j}>
                {parts.map((pt, n) => <div key={n} className={'pg-mask ' + pt.cls} style={{ top: pt.top, height: pt.bottom - pt.top, left: x, width: 8 }} />)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ листы перед текстом
/** Низ содержимого листа (px без масштаба от верха листа). */
function contentBottomOf(el: HTMLElement): number {
  const r = el.getBoundingClientRect();
  const k = r.width / (el.offsetWidth || 1) || 1;
  let bottom = 0;
  for (const c of Array.from(el.children) as HTMLElement[]) {
    const cs = getComputedStyle(c);
    if (cs.position === 'absolute' || cs.position === 'fixed' || cs.display === 'none') continue;
    bottom = Math.max(bottom, (c.getBoundingClientRect().bottom - r.top) / k + (parseFloat(cs.marginBottom) || 0));
  }
  return bottom;
}

/** Сколько страниц занимает лист перед текстом. Текст малого редактора разбит на страницы (front.ts, атрибут
 *  data-flow-pages) - по положению низа содержимого; иначе - оценка по высоте (содержимое выше 257 мм). */
function pagesOf(el: HTMLElement): number {
  const bottom = contentBottomOf(el);
  if (el.hasAttribute('data-flow-pages')) return Math.max(1, Math.floor(Math.max(0, bottom - 1) / STRIDE) + 1);
  return Math.max(1, Math.ceil((bottom - M_TOP) / TEXT_H - 0.02));
}

/** Абзацы листа вне малого редактора после разбитого на страницы текста (подпись реферата): строка, которая попала бы
 *  в нижнее поле или промежуток между листами, переносится в начало следующего листа (как в Word). */
function pushTail(el: HTMLElement) {
  const tail = Array.from(el.querySelectorAll('.pg-keep')) as HTMLElement[];
  if (!tail.length) return;
  const r = el.getBoundingClientRect();
  const k = r.width / (el.offsetWidth || 1) || 1;
  for (const t of tail) {
    const cur = t.style.marginTop;
    t.style.marginTop = '';
    const tr = t.getBoundingClientRect();
    const top = (tr.top - r.top) / k;
    const bottom = (tr.bottom - r.top) / k;
    const i = Math.floor(top / STRIDE);
    const limit = i * STRIDE + PAGE_H - M_BOTTOM + 0.5;    // нижнее поле 20 мм
    let m = '';
    if (bottom > limit) m = ((i + 1) * STRIDE + M_TOP - top + (parseFloat(getComputedStyle(t).marginTop) || 0)) + 'px';
    void cur;
    t.style.marginTop = m;
  }
}

const frontCounts = new Map<string, { pages: number; numbered: boolean; order: number }>();
function publishFront() {
  const list = [...frontCounts.entries()].sort((a, b) => a[1].order - b[1].order).map(([id, v]) => ({ id, pages: v.pages, numbered: v.numbered }));
  const total = list.reduce((s, x) => s + x.pages, 0);
  const s = pagesStore.getState();
  if (s.frontCount !== total || JSON.stringify(s.front) !== JSON.stringify(list)) s.set({ front: list, frontCount: total, total: total + s.main.length });
}

/** Рамка листа перед текстом: A4; если текст длиннее - несколько отдельных листов A4 подряд (с промежутком, как
 *  листы основного текста), текст малого редактора перетекает с листа на лист (front.ts). */
export function FrontSheetFrame({ id, order, children }: { id: string; order: number; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [n, setN] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const upd = () => {
      if (id !== 'toc') pushTail(el);
      const p = id === 'toc' ? Number(el.getAttribute('data-pages') || 1) : pagesOf(el);
      setN(p);
      frontCounts.set(id, { pages: p, numbered: id === 'toc', order });
      publishFront();
    };
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    for (const c of Array.from(el.children)) ro.observe(c);
    const mo = new MutationObserver(upd);
    mo.observe(el, { attributes: true, attributeFilter: ['data-pages', 'data-flow-pages'] });
    return () => { ro.disconnect(); mo.disconnect(); frontCounts.delete(id); publishFront(); };
  }, [id, order]);
  if (id === 'toc') {
    return <div ref={ref} className="pg-front-multi" data-sheet={id} data-pages="1">{children}</div>;
  }
  return (
    <div ref={ref} className={'sheet sheet-front pg-front' + (n > 1 ? ' pg-front-paged' : '')} data-sheet={id} data-pages={n}
      style={{ minHeight: n * PAGE_H + (n - 1) * (STRIDE - PAGE_H) }}>
      {n > 1 && (
        <div className="pg-front-bg" aria-hidden>
          {Array.from({ length: n }, (_, i) => <div key={i} className="pg-page" style={{ top: i * STRIDE }} />)}
        </div>
      )}
      {children}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ оглавление
interface TocRow { id: string; text: string; level: number }

/** Оглавление по СТО (как поле TOC у writer): заголовки 1-2 уровня, структурные элементы, приложения; номера
 *  страниц - из разметки; длинное оглавление - на нескольких листах. */
export function TocPages({ ctx }: { ctx: Ctx }) {
  const nb = useNumbering();
  const m = useApp((s) => s.meta);
  const heads = usePages((s) => s.heads);
  const front = usePages((s) => s.front);
  const ready = usePages((s) => s.ready);
  const rows: TocRow[] = useMemo(() => nb.order.map((id) => [id, nb.byId.get(id)!] as const)
    .filter(([, i]) => i && ((i.kind === 'sec' && (i.level || 1) <= 3 && i.num) || i.kind === 'struct' || i.kind === 'app'))
    .map(([id, i]) => ({
      id, level: i.kind === 'sec' ? (i.level || 1) : 1,
      text: i.kind === 'app' ? `${i.label.replace(NB, ' ')} (${i.status}) ${i.title}` : i.kind === 'sec' ? `${i.num} ${i.title}` : i.title,
    })), [nb]);
  const meas = useRef<HTMLDivElement>(null);
  const [split, setSplit] = useState<number[]>([rows.length]);
  useLayoutEffect(() => {
    const el = meas.current;
    if (!el) return;
    const title = el.querySelector('.pg-toc-title') as HTMLElement | null;
    let used = title ? title.offsetHeight + parseFloat(getComputedStyle(title).marginBottom || '0') : 0;
    const cuts: number[] = [];
    const items = Array.from(el.querySelectorAll('.pg-toc-row')) as HTMLElement[];
    items.forEach((it, i) => {
      const h = it.offsetHeight;
      if (used + h > BODY_H + 0.5 && i > 0) { cuts.push(i); used = 0; }
      used += h;
    });
    // после поля TOC в DOCX остаётся пустой абзац (знак конца поля): если он не помещается - Word даёт ещё лист
    const tail = el.querySelector('.pg-toc-tail') as HTMLElement | null;
    if (tail && used + tail.offsetHeight > BODY_H + 0.5) cuts.push(rows.length);
    cuts.push(rows.length);
    if (cuts.join() !== split.join()) setSplit(cuts);
    const host = el.closest('.pg-front-multi');
    if (host && host.getAttribute('data-pages') !== String(cuts.length)) host.setAttribute('data-pages', String(cuts.length));
  });
  if (!m || !tocOn(m)) return null;                  // оглавление выключено (meta.toc_page, где СТО разрешает)
  const frontBefore = (() => {
    let n = 0;
    for (const f of front) { if (f.id === 'toc') break; n += f.pages; }
    return n;
  })();
  const frontCount = front.reduce((s, f) => s + f.pages, 0);
  const pageNo = (id: string) => (ready && heads[id] !== undefined ? String(frontCount + heads[id]) : '…');
  const row = (r: TocRow) => (
    <div key={r.id} className={'pg-toc-row lvl-' + r.level} onClick={() => (r.id === 'meta_ai' ? ctx.openDialog('sto.meta', { tab: 'ai' }) : ctx.editor && gotoNode(ctx.editor, r.id))}
      data-testid="toc-row">
      <span className="pg-toc-t">{r.text}</span><span className="pg-toc-pn">{pageNo(r.id)}</span>
    </div>
  );
  const chunks: TocRow[][] = [];
  let from = 0;
  for (const c of split) { chunks.push(rows.slice(from, c)); from = c; }
  if (!chunks.length) chunks.push([]);
  return (
    <>
      <div className="pg-toc-measure sheet" ref={meas} aria-hidden>
        <div className="pg-toc-title">Оглавление</div>
        {rows.map(row)}
        <div className="pg-toc-tail">{NB}</div>
      </div>
      {chunks.map((ch, i) => (
        <div key={i} className="sheet sheet-front pg-front pg-toc-sheet" data-sheet="toc" data-testid={i === 0 ? 'toc-sheet' : undefined} style={{ minHeight: PAGE_H }}>
          {i === 0 && <div className="pg-toc-title">Оглавление</div>}
          {i === 0 && !rows.length && <div className="muted pg-toc-empty">Оглавление появится, когда в тексте будут заголовки.</div>}
          {ch.map(row)}
          <div className="pg-num" style={{ top: FOOTER_BOTTOM - LINE_14_SINGLE, height: LINE_14_SINGLE }}>{frontBefore + i + 1}</div>
        </div>
      ))}
    </>
  );
}

/** Пересчитать номера страниц оглавления после сборки листов (эффект без разметки). */
export function useFrontPublish() {
  useEffect(() => { publishFront(); }, []);
}
